import {
  CircularProgress, Grid,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  selectContext, selectContextError, selectContextStatus, exitSelectionMode,
} from '../redux/features/context/contextSlice';
import {
  fetchAuthors, deleteAuthor, updateAuthor, mergeAuthors, shareAuthor,
} from '../redux/features/authors/authorsSlice';
import {
  approveAuthor, rejectAuthor, batchModerateAuthors,
} from '../redux/features/pending/pendingSlice';
import { shadowBleed } from '../components/common/cardShadow';
import { HeaderSpacer } from '../components/Header';
import StyledBackdrop from '../components/StyledBackdrop';
import ViewError from '../components/common/ViewError';
import useLoadErrorHandler from '../hooks/useLoadErrorHandler';
import AuthorCardGrid from '../components/AuthorCardGrid';
import FilterBar from '../components/FilterBar';
import SelectionBar, { SelectionBarVerdictButton } from '../components/SelectionBar';
import { RejectReasonMenu } from '../components/common/VerdictBar';
import Login from '../components/Login';
import { DELETE_LABEL, UNSHARE_LABEL } from '../common/ActionLabels';
import {
  getAuthorName, mergeAuthor, mustSignIn, shareResultMessage,
} from '../common/ItemTools';
import { setSnackbar } from '../redux/features/snackbarSlice';
import { batchVerdictMessage, verdictPastTense, verdictResultMessage } from '../common/VerdictMessages';
import useGridLayout from '../hooks/useGridLayout';
import useConfirm from '../hooks/useConfirm';
import useBatchSelection from '../hooks/useBatchSelection';
import usePaging from '../hooks/usePaging';

function AuthorsView(props) {
  const type = 'authors';
  const { namespace } = props;
  const { userInfo } = useSelector((state) => state.users);

  const [searchParams] = useSearchParams();
  const status = useSelector(selectContextStatus);
  const loadError = useSelector(selectContextError);
  const onLoadError = useLoadErrorHandler('authors');
  const currContext = useSelector(selectContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pageSize } = useGridLayout();
  const { confirm, confirmDialog } = useConfirm();

  const page = +searchParams.get('page');
  const filter = searchParams.get('filter');
  const sortby = searchParams.get('sortby');
  const source = searchParams.get('source');
  const view = searchParams.get('view');

  const forceSignIn = mustSignIn(namespace, page, userInfo);

  const context = (!forceSignIn
    && currContext
    && currContext.namespace === namespace
    && currContext.type === type) ? currContext : null;

  // Extract all URL parameters - single source of truth
  const urlParams = useMemo(() => ({
    page: page >= 1 ? page : 1,
    filter,
    sortby,
    source,
    view,
  }), [page, filter, sortby, source, view]);

  // Fetch authors based on current URL params
  const fetchCurrentAuthors = useCallback(() => {
    if (forceSignIn) return;

    const pageNumber = urlParams.page;
    const offset = (pageNumber - 1) * pageSize;

    const params = {
      pageNumber,
      offset,
      limit: pageSize,
      ...(urlParams.filter && { filter: urlParams.filter }),
      ...(urlParams.sortby && { sortby: urlParams.sortby }),
      ...(urlParams.source && { source: urlParams.source }),
      ...(urlParams.view && { view: urlParams.view }),
    };

    dispatch(fetchAuthors({ namespace, params }))
      .unwrap()
      .catch(onLoadError);
  }, [dispatch, namespace, urlParams, forceSignIn, pageSize, onLoadError]);

  // Single effect: URL changes → fetch data
  useEffect(() => {
    if (forceSignIn) return;
    fetchCurrentAuthors();
  }, [
    urlParams.page,
    urlParams.filter,
    urlParams.sortby,
    urlParams.source,
    urlParams.view,
    namespace,
    userInfo,
    forceSignIn,
    fetchCurrentAuthors,
  ]);

  const isModerating = namespace === 'pending';
  const showDelete = Boolean(userInfo) && !isModerating;
  const showUnshare = Boolean(userInfo?.isModerator) && !isModerating;
  const [bulkRejectAnchor, setBulkRejectAnchor] = useState(null);

  const pageAuthors = useMemo(
    () => (context?.authors || []).map(mergeAuthor).filter(Boolean),
    [context],
  );
  const {
    selectionMode, selectedIds, selectedCount,
    pageIds, selectAllLabel, selectPage, selectAll, clear: clearSelection,
  } = useBatchSelection(pageAuthors);

  const onAuthorRead = (author) => {
    navigate(isModerating ? `/moderate/authors/${author.id}` : `/authors/${author.id}`);
  };
  const onAuthorDelete = async (author) => {
    const ok = await confirm({
      title: 'Delete author?',
      message: `"${getAuthorName(author) || 'This author'}" will be permanently removed from your library.`,
      confirmLabel: DELETE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteAuthor({ namespace: 'private', authorId: author.id }))
      .unwrap()
      .then(() => {
        // Refresh current view after deleting
        fetchCurrentAuthors();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to delete author.',
        }));
      });
  };
  const onAuthorShare = (author) => {
    dispatch(shareAuthor({ authorId: author.id }))
      .unwrap()
      .then((result) => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: shareResultMessage(result),
        }));
        fetchCurrentAuthors();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to share the author.',
        }));
      });
  };
  const onAuthorUnshare = async (author) => {
    const ok = await confirm({
      title: 'Unshare author?',
      message: `"${getAuthorName(author) || 'This author'}" will be removed from the public record. Creators keep their private copies.`,
      confirmLabel: UNSHARE_LABEL,
      destructive: true,
    });
    if (!ok) return;
    dispatch(deleteAuthor({ namespace: 'public', authorId: author.id }))
      .unwrap()
      .then(() => {
        fetchCurrentAuthors();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to unshare author.',
        }));
      });
  };
  const onAuthorVerdict = (author, kind, reason) => {
    const thunk = kind === 'approve'
      ? approveAuthor(author)
      : rejectAuthor({ id: author.id, reason });
    dispatch(thunk)
      .unwrap()
      .then(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: verdictResultMessage(kind, 'author', getAuthorName(author), reason),
        }));
        fetchCurrentAuthors();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: `Failed to ${kind} author.`,
        }));
        fetchCurrentAuthors();
      });
  };

  const onBatchVerdict = async (kind, reason = null) => {
    if (selectedCount === 0) return;
    try {
      const { failed } = await dispatch(
        batchModerateAuthors({ ids: selectedIds, verdict: kind, reason }),
      ).unwrap();
      if (failed.length > 0) {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'warning',
          snackbarMessage: `${failed.length} of ${selectedIds.length} authors could not be ${verdictPastTense(kind)}.`,
        }));
      } else {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'success',
          snackbarMessage: batchVerdictMessage(kind, 'author', selectedIds.length, reason),
        }));
      }
    } catch (error) {
      dispatch(setSnackbar({
        snackbarOpen: true,
        snackbarType: 'error',
        snackbarMessage: `Failed to ${kind} authors.`,
      }));
    } finally {
      dispatch(exitSelectionMode());
      fetchCurrentAuthors();
    }
  };

  const onAuthorEdit = (author) => {
    navigate(isModerating
      ? `/moderate/authors/${author.id}?edit`
      : `/edit/author/${author.id}`);
  };

  const onAuthorMerge = (targetAuthorId, sourceAuthorIds) => {
    dispatch(mergeAuthors({ namespace, targetAuthorId, sourceAuthorIds }))
      .unwrap()
      .then(() => {
        fetchCurrentAuthors();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to merge authors.',
        }));
      });
  };

  const onAuthorFavorite = (author) => {
    const patch = { isFavorite: !author.isFavorite };
    dispatch(updateAuthor({ namespace: 'private', authorId: author.id, patch }))
      .unwrap()
      .then(() => {
        // Refresh current view after marking as favorite
        fetchCurrentAuthors();
      })
      .catch(() => {
        dispatch(setSnackbar({
          snackbarOpen: true,
          snackbarType: 'error',
          snackbarMessage: 'Failed to favorite author.',
        }));
      });
  };

  const { handleNextPage, handlePreviousPage } = usePaging(urlParams.page);

  return (
    <>
      <HeaderSpacer />
      { !forceSignIn && <FilterBar entity="authors" /> }
      {confirmDialog}
      { forceSignIn && <Login />}
      { !forceSignIn && (
        <StyledBackdrop open={status === 'loading' || (!context && status !== 'failed')}>
          <CircularProgress color="inherit" />
        </StyledBackdrop>
      )}
      { !forceSignIn && !context && status === 'failed' && (
        <ViewError message={loadError} entity="authors" />
      )}
      { !forceSignIn && context && status !== 'loading' && (
        <Grid container sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', ...shadowBleed }}>
          <Grid container item sx={{ flex: 1, ...shadowBleed }}>
            <Grid xs={12} item sx={{ height: '100%' }}>
              <AuthorCardGrid
                authorActions={{
                  ...(isModerating && { onAuthorVerdict }),
                  ...(showDelete && { onAuthorDelete }),
                  ...(showUnshare && { onAuthorUnshare }),
                  onAuthorRead,
                  onAuthorEdit,
                  ...(!isModerating && Boolean(userInfo) && { onAuthorFavorite }),
                  ...(!isModerating && { onAuthorShare }),
                  onAuthorMerge,
                }}
                currentPage={context}
                isReadOnly={false}
                listActions={{
                  handlePreviousPage,
                  handleNextPage,
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      { isModerating && selectionMode && (
        <SelectionBar
          total={pageIds.length}
          selectAllLabel={selectAllLabel}
          onSelectPage={selectPage}
          onSelectAll={selectAll}
          onClear={clearSelection}
          actions={(
            <>
              <SelectionBarVerdictButton kind="approve" onClick={() => onBatchVerdict('approve')}>
                <CheckIcon sx={{ fontSize: 17 }} />
                Approve
              </SelectionBarVerdictButton>
              <SelectionBarVerdictButton
                kind="reject"
                onClick={(event) => setBulkRejectAnchor(event.currentTarget)}
              >
                <CloseIcon sx={{ fontSize: 17 }} />
                Reject
              </SelectionBarVerdictButton>
              <RejectReasonMenu
                anchorEl={bulkRejectAnchor}
                title={`Reject ${selectedCount} as`}
                onClose={() => setBulkRejectAnchor(null)}
                onPick={(reason) => onBatchVerdict('reject', reason)}
              />
            </>
          )}
        />
      )}
    </>
  );
}

export default AuthorsView;
